import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, ArrowLeft, Pencil } from 'lucide-react';
import DualScrollTable from '../../../components/common/DualScrollTable';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';
import { MarketReview } from '../../../types/market-review';
import AddMarketReviewModal from './components/AddMarketReviewModal';
import EditMarketReviewModal from './components/EditMarketReviewModal';
import ExpandableText from '../../../components/common/ExpandableText';

export default function MarketReviewsAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<MarketReview | null>(null);

  // Fetch review types to get the descriptions
  const { data: reviewTypes } = useQuery({
    queryKey: ['review_types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_review_types')
        .select('review_type, description');
        
      if (error) {
        console.error('Error fetching review types:', error);
        throw error;
      }
      
      return data || [];
    }
  });
  
  // Create a mapping of review type codes to descriptions
  const reviewTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (reviewTypes) {
      reviewTypes.forEach(type => {
        map[type.review_type] = type.description;
      });
    }
    return map;
  }, [reviewTypes]);
  
  const { data: reviews, isLoading } = useQuery<MarketReview[]>({
    queryKey: ['admin', 'market_reviews'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('market_reviews')
        .select('*')
        .order('review_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data;
    }
  });

  const filteredReviews = reviews?.filter(review => 
    review.review_lot?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    review.review_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (review.review_comment?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'market_reviews'] });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Manage Market Reviews</h1>
            <p className="mt-1 text-sm text-gray-500">
              Add, edit, and manage market review records
            </p>
          </div>
          <button 
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Market Review
          </button>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-4 items-center flex-1">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search market reviews..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <DualScrollTable>
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Edit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Review Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  UCV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Review
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                  Notes and Docs
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredReviews?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No market review records found
                  </td>
                </tr>
              ) : (
                filteredReviews?.map((review) => (
                  <tr key={`${review.review_lot}-${review.review_date}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button 
                        onClick={() => {
                          setSelectedReview(review);
                          setIsEditModalOpen(true);
                        }}
                        className="text-primary hover:text-primary-dark"
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {review.review_lot}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {review.review_date ? formatDate(review.review_date) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {review.review_type ? reviewTypeMap[review.review_type] || review.review_type : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {review.review_ucv ? `$${review.review_ucv.toLocaleString()}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {review.review_next ? formatDate(review.review_next) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      <ExpandableText 
                        text={review.review_comment || 'Not specified'} 
                        maxLength={40}
                        className="w-64"
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
      </div>

      <AddMarketReviewModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleSuccess}
      />

      {selectedReview && (
        <EditMarketReviewModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedReview(null);
          }}
          onSuccess={handleSuccess}
          review={selectedReview}
        />
      )}
    </div>
  );
}
